import { CheckCircleOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Form, Input } from 'antd';
import notification from 'antd/es/notification';
import ModuleCard from 'components/cards/ModuleCard';
import { useUpdateLicenseCode } from 'hooks/useUpdateLicenseCode';
import posthog from 'posthog-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { selectCurrentUser } from 'store/users/selectors';
import { Button } from 'components/ui';
import { colors } from 'design';

const useStyles = createUseStyles({
  section: {
    width: '100%',
  },
  message: {
    margin: 0,
    color: colors.text.tertiary,
  },
  form: {
    width: '100%',
    marginTop: 16,
    '& .ant-form-item-label': {
      padding: 0,
      '& label': {
        color: colors.text.tertiary,
      },
    },
  },
  licenseRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  licenseInputItem: {
    flex: 1,
    marginBottom: 0,
  },
  formsContainer: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: 120,
  },
  formPanel: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transition: 'transform 0.3s ease, opacity 0.3s ease',
  },
  formButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 24,
  },
  collapseButton: {
    padding: 0,
    height: 'auto',
    fontSize: '0.85em',
    color: colors.text.tertiary,
  },
  partnerRequestMessage: {
    marginTop: 16,
    color: colors.brand.accentStrong,
    fontSize: '0.9em',
  },
  successRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    marginBottom: 8,
    padding: '0px 32px',
    textAlign: 'center',
    '& .anticon': {
      marginBottom: 16,
    },
  },
});

export default function PartnerSection() {
  const styles = useStyles();
  const { t } = useTranslation();
  const [api, context] = notification.useNotification();
  const currentUser = useSelector(selectCurrentUser);
  const [codeForm] = Form.useForm();
  const [companyForm] = Form.useForm();
  const [isAskingCompanyName, setIsAskingCompanyName] = useState(false);
  const [partnerRequestSubmitted, setPartnerRequestSubmitted] = useState(false);
  const [licenseSaved, setLicenseSaved] = useState(false);

  const { run: updateLicenseCode, loading: loadingLicenseCode } = useUpdateLicenseCode({
    api,
    onSuccess: () => {
      setLicenseSaved(true);
      codeForm.resetFields();
    },
    posthogSource: 'homepage',
  });

  const { run: createPartnerRequest, loading: loadingPartnerRequest } = useRequest(
    (formValues: { companyName: string }) =>
      ApiUsersManager.createPartnerRequest({ company_name: formValues.companyName }),
    {
      manual: true,
      onSuccess: () => {
        setPartnerRequestSubmitted(true);
        api.success({
          message: t('settings.partner.requestCreated'),
        });
        posthog.capture('settings.partner_request_created', { source: 'homepage' });
      },
      onError: () => {
        api.error({
          message: t('settings.license.errors.default'),
        });
      },
    },
  );

  // Hide the section for users who already have a license code on page load,
  // but keep showing it right after a successful activation so we can display
  // the confirmation message.
  const hasLicenseCode = !!currentUser?.license_code && !licenseSaved;

  if (hasLicenseCode || currentUser?.is_caregiver) {
    // User has a license code: do not show this section
    return null;
  }

  return (
    <div className={styles.section}>
      {context}
      <ModuleCard title={t('home.partner.title')}>
        {licenseSaved && (
          <div className={styles.successRow}>
            <CheckCircleOutlined style={{ color: colors.brand.accentStrong, fontSize: 40 }} />
            <span>{t('home.partner.successMessage')}</span>
          </div>
        )}

        {!licenseSaved && (
          <>
            <p className={styles.message}>
              {isAskingCompanyName ? t('home.partner.companyNameDescription') : t('home.partner.codeDescription')}
            </p>
            {!partnerRequestSubmitted && (
              <div className={styles.formsContainer}>
                <div
                  className={styles.formPanel}
                  style={{
                    transform: isAskingCompanyName ? 'translateX(-100%)' : 'translateX(0)',
                    opacity: isAskingCompanyName ? 0 : 1,
                    pointerEvents: isAskingCompanyName ? 'none' : 'auto',
                  }}
                >
                  <Form
                    form={codeForm}
                    onFinish={values => {
                      updateLicenseCode(values as { licenseCode: string });
                    }}
                    className={styles.form}
                    initialValues={{ licenseCode: (currentUser?.license_code || '').toUpperCase() }}
                  >
                    <>
                      <div className={styles.licenseRow}>
                        <Form.Item name="licenseCode" className={styles.licenseInputItem}>
                          <Input
                            type="text"
                            placeholder={t('home.partner.codePlaceholder')}
                            // style={{ textTransform: 'uppercase' }}
                          />
                        </Form.Item>
                        <Button variant="primary" htmlType="submit" loading={loadingLicenseCode}>
                          {t('common.confirm')}
                        </Button>
                      </div>
                      <div className={styles.formButtons}>
                        <Button
                          block
                          size="lg"
                          className={styles.collapseButton}
                          onClick={() => {
                            codeForm.resetFields();
                            setIsAskingCompanyName(true);
                          }}
                        >
                          {t('home.partner.dontKnowCode')}
                        </Button>
                      </div>
                    </>
                  </Form>
                </div>

                <div
                  className={styles.formPanel}
                  style={{
                    transform: isAskingCompanyName ? 'translateX(0)' : 'translateX(100%)',
                    opacity: isAskingCompanyName ? 1 : 0,
                    pointerEvents: isAskingCompanyName ? 'auto' : 'none',
                  }}
                >
                  <Form
                    form={companyForm}
                    onFinish={values => {
                      createPartnerRequest(values as { companyName: string });
                    }}
                    className={styles.form}
                  >
                    <>
                      <div className={styles.licenseRow}>
                        <Form.Item
                          name="companyName"
                          className={styles.licenseInputItem}
                          rules={[{ required: true, message: t('home.partner.companyNameRequired') }]}
                        >
                          <Input placeholder={t('home.partner.companyNamePlaceholder')} />
                        </Form.Item>
                        <Button variant="primary" htmlType="submit" loading={loadingPartnerRequest}>
                          {t('common.confirm')}
                        </Button>
                      </div>
                      <div className={styles.formButtons}>
                        <Button
                          block
                          size="lg"
                          className={styles.collapseButton}
                          onClick={() => {
                            companyForm.resetFields();
                            setIsAskingCompanyName(false);
                          }}
                        >
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </>
                  </Form>
                </div>
              </div>
            )}
          </>
        )}
        {partnerRequestSubmitted && !licenseSaved && (
          <p className={styles.partnerRequestMessage}>{t('home.partner.requestSubmittedMessage')}</p>
        )}
      </ModuleCard>
    </div>
  );
}
